import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, defer, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PlatformService } from './platform.service';

export interface OauthTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  private readonly storage_verifier_key = 'bisual-oauth-code-verifier';
  private readonly storage_state_key = 'bisual-oauth-state';
  private readonly storage_redirect_key = 'bisual-oauth-post-login-redirect';

  private http = inject(HttpClient);
  private platform = inject(PlatformService);

  beginLogin(post_login_redirect: string | null = null): void {
    const code_verifier = this.generateCodeVerifier();
    const state = this.generateRandomString(32);

    sessionStorage.setItem(this.storage_verifier_key, code_verifier);
    sessionStorage.setItem(this.storage_state_key, state);

    if (post_login_redirect) {
      this.rememberPostLoginRedirect(post_login_redirect);
    }

    void this.buildAuthorizeUrl(code_verifier, state).then(url => {
      if (this.platform.isElectronApp()) {
        window.electron.openExternalLink(url);
        return;
      }

      window.location.href = url;
    });
  }

  exchangeCode(code: string, state: string): Observable<OauthTokenResponse> {
    return defer((): Observable<OauthTokenResponse> => {
      const expected_state = sessionStorage.getItem(this.storage_state_key);
      const code_verifier = sessionStorage.getItem(this.storage_verifier_key);

      if (!expected_state || state !== expected_state) {
        return throwError(() => new Error('Invalid OAuth state'));
      }

      if (!code_verifier) {
        return throwError(() => new Error('Missing PKCE code verifier'));
      }

      const body = new HttpParams()
        .set('grant_type', 'authorization_code')
        .set('client_id', environment.oauth_client_id)
        .set('redirect_uri', this.redirectUri())
        .set('code_verifier', code_verifier)
        .set('code', code);

      return this.http.post<OauthTokenResponse>(`${environment.auth_url}/oauth/token`, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          'avoid-snackbar-notification': 'true',
        }),
      });
    });
  }

  refresh(refresh_token: string): Observable<OauthTokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refresh_token)
      .set('client_id', environment.oauth_client_id)
      .set('scope', '');

    return this.http.post<OauthTokenResponse>(`${environment.auth_url}/oauth/token`, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'avoid-snackbar-notification': 'true',
        'avoid-loading': 'true',
      }),
    });
  }

  consumePostLoginRedirect(): string | null {
    const redirect = sessionStorage.getItem(this.storage_redirect_key);
    sessionStorage.removeItem(this.storage_redirect_key);
    sessionStorage.removeItem(this.storage_verifier_key);
    sessionStorage.removeItem(this.storage_state_key);

    return redirect;
  }

  rememberPostLoginRedirect(redirect: string): void {
    if (redirect === '' || redirect.startsWith('/auth')) {
      return;
    }

    sessionStorage.setItem(this.storage_redirect_key, redirect);
  }

  peekPostLoginRedirect(): string | null {
    return sessionStorage.getItem(this.storage_redirect_key);
  }

  clearPkceSession(): void {
    sessionStorage.removeItem(this.storage_verifier_key);
    sessionStorage.removeItem(this.storage_state_key);
  }

  private redirectUri(): string {
    if (this.platform.isElectronApp()) {
      return `${environment.electron_schema}://auth/oauth/callback`;
    }

    return `${environment.angular_url}/auth/oauth/callback`;
  }

  private async buildAuthorizeUrl(code_verifier: string, state: string): Promise<string> {
    const code_challenge = await this.generateCodeChallenge(code_verifier);
    const params = new URLSearchParams({
      client_id: environment.oauth_client_id,
      redirect_uri: this.redirectUri(),
      response_type: 'code',
      scope: '',
      state,
      code_challenge: code_challenge,
      code_challenge_method: 'S256',
    });

    return `${environment.auth_url}/oauth/authorize?${params.toString()}`;
  }

  private generateCodeVerifier(): string {
    return this.generateRandomString(64);
  }

  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(buffer: Uint8Array): string {
    let binary = '';
    buffer.forEach(byte => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
